import { CloseCircleFilled, EditOutlined, WarningFilled } from "@ant-design/icons";
import { Alert, Button, Card, Flex, Skeleton, Tag, Typography } from "antd";
import type { ReactNode } from "react";
import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { type ComplianceFinding, useGetComplianceForNoteQuery } from "../../service/compliance.service";
import { setShowSettingsModal } from "../../slices/appStateSlice";
import { TONE } from "./findings.shared";

type Props = {
  audioFileId: number;
};

// Visual treatment matches the note container chrome (NoteTitleHeader):
// soft shadow on the outer card, gray header strip with 1px bottom border.
// Header padding is tighter than the note body to keep this artifact compact.
const CARD_STYLE = {
  marginBottom: 20,
  boxShadow: "0px 4px 4px 0px #0000000a",
};
const CARD_BODY_STYLE = { background: "#fff", padding: 12 };
const CARD_HEADER_STYLE = {
  backgroundColor: "#fafafc",
  borderBottom: "1px solid #f0f0f0",
  padding: "10px 15px",
  minHeight: 0,
};

const DESCRIPTION_PREVIEW_CHARS = 120;
const VISIBLE_FINDINGS_DEFAULT = 3;

const ADMIN_TAG_STYLE = {
  margin: 0,
  fontSize: 10,
  fontWeight: 500,
  color: "#4338ca",
  background: "#eef2ff",
  borderColor: "#c7d2fe",
  whiteSpace: "nowrap" as const,
  flexShrink: 0,
};

const Shell = ({ subtitle, children }: { subtitle?: string; children: ReactNode }) => (
  <Card
    style={CARD_STYLE}
    styles={{ body: CARD_BODY_STYLE, header: CARD_HEADER_STYLE }}
    title={
      <Flex justify="space-between" align="center" gap={8}>
        <Flex align="center" gap={8} style={{ minWidth: 0 }}>
          <Typography.Text strong style={{ fontSize: 14, whiteSpace: "nowrap" }}>
            Compliance Review
          </Typography.Text>
          {subtitle ? (
            <Typography.Text style={{ fontSize: 12, color: "#6b7280", fontWeight: 400, whiteSpace: "nowrap" }}>
              · {subtitle}
            </Typography.Text>
          ) : null}
        </Flex>
        <Tag style={ADMIN_TAG_STYLE}>Set by your admin</Tag>
      </Flex>
    }
  >
    {children}
  </Card>
);

const FindingRow = ({ finding }: { finding: ComplianceFinding }) => {
  const [expanded, setExpanded] = useState(false);
  const isFail = finding.judgment === "fail";
  const fullDescription = finding.description;
  const isTruncatable = fullDescription.length > DESCRIPTION_PREVIEW_CHARS;
  const displayedDescription =
    expanded || !isTruncatable ? fullDescription : `${fullDescription.slice(0, DESCRIPTION_PREVIEW_CHARS).trim()}…`;

  return (
    <Flex align="flex-start" gap={12} style={{ padding: "10px 0", borderBottom: "1px solid #eef0f3" }}>
      {isFail ? (
        <CloseCircleFilled style={{ color: TONE.danger.accent, fontSize: 16, marginTop: 3 }} />
      ) : (
        <WarningFilled style={{ color: TONE.warning.accent, fontSize: 16, marginTop: 3 }} />
      )}
      <Flex vertical flex={1} gap={2}>
        <Typography.Text strong style={{ fontSize: 13 }}>
          {finding.rule_name}
        </Typography.Text>
        <Typography.Text style={{ color: "#4b5563", fontSize: 12, lineHeight: 1.5 }}>
          {displayedDescription}
          {isTruncatable ? (
            <Button
              type="link"
              size="small"
              onClick={() => setExpanded((v) => !v)}
              style={{ padding: "0 0 0 4px", height: "auto", fontSize: 12 }}
            >
              {expanded ? "Show less" : "Show more"}
            </Button>
          ) : null}
        </Typography.Text>
      </Flex>
    </Flex>
  );
};

const ComplianceReview = ({ audioFileId }: Props) => {
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector((state) => state.auth);
  const [showAllFindings, setShowAllFindings] = useState(false);

  // Group-level opt-in: admins toggle `show_compliance_review_on_note` to
  // surface this artifact above the clinician's note. Default off.
  const shouldShow = !!userInfo?.show_compliance_review_on_note;
  const { data, isLoading } = useGetComplianceForNoteQuery(audioFileId, { skip: !shouldShow });

  if (!shouldShow) return null;

  if (isLoading || data?.status === "pending") {
    return (
      <Shell>
        <Skeleton active title={false} paragraph={{ rows: 2 }} />
      </Shell>
    );
  }

  // Hide entirely on failed evaluation, when the task hasn't produced a
  // result (no rules attached, or the task hasn't been queued), or when
  // everything passed — clinicians only see this artifact when there's
  // something to act on.
  if (!data || data.status === "failed" || data.review_count === 0) return null;

  const { findings, review_count, specialty_used } = data;

  const visibleFindings = showAllFindings ? findings : findings.slice(0, VISIBLE_FINDINGS_DEFAULT);
  const hiddenCount = findings.length - visibleFindings.length;

  return (
    <Shell subtitle={`${review_count} to review`}>
      {specialty_used ? (
        <Alert
          type="warning"
          message={`Specialty noted for additional context: ${specialty_used}`}
          style={{
            marginBottom: 12,
            color: TONE.warning.fg,
            background: TONE.warning.bg,
            border: `1px solid ${TONE.warning.border}`,
          }}
          action={
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => dispatch(setShowSettingsModal(true))}
              style={{ color: TONE.warning.fg }}
            >
              Edit
            </Button>
          }
        />
      ) : null}

      <Flex vertical>
        {visibleFindings.map((finding) => (
          <FindingRow key={finding.id} finding={finding} />
        ))}
      </Flex>

      {hiddenCount > 0 ? (
        <Flex justify="center" style={{ marginTop: 8 }}>
          <Button type="link" size="small" onClick={() => setShowAllFindings(true)}>
            Show {hiddenCount} more
          </Button>
        </Flex>
      ) : findings.length > VISIBLE_FINDINGS_DEFAULT ? (
        <Flex justify="center" style={{ marginTop: 8 }}>
          <Button type="link" size="small" onClick={() => setShowAllFindings(false)}>
            Show less
          </Button>
        </Flex>
      ) : null}
    </Shell>
  );
};

export default ComplianceReview;
